import { FastArrowLeft, FastArrowRight, NavArrowLeft, NavArrowRight } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import { useTheme } from 'components/core/theme-provider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';

/** Same page sizes as the IAM table. */
const PAGE_SIZES = [10, 25, 50];

type TeamsPeoplePaginationProps = {
  page: number;
  pageCount: number;
  pageSize: number;
  rangeStart: number;
  rangeEnd: number;
  totalCount: number;
  isDisabled: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
};

export function TeamsPeoplePagination({
  page,
  pageCount,
  pageSize,
  rangeStart,
  rangeEnd,
  totalCount,
  isDisabled,
  onPageChange,
  onPageSizeChange,
}: TeamsPeoplePaginationProps) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const valueColor = theme === 'dark' ? 'text-true-white' : 'text-grey-900';
  const headerColor = theme === 'dark' ? 'text-grey-300' : 'text-grey-600';
  const borderColor = theme === 'dark' ? 'border-border' : 'border-grey-100';

  const isFirstPage = page === 0 || isDisabled;
  const isLastPage = page >= pageCount - 1 || totalCount === 0 || isDisabled;
  const pageButtonClassName = cn(
    'flex size-9 items-center justify-center rounded-card border bg-true-white disabled:opacity-40 dark:bg-card',
    borderColor
  );

  return (
    <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-2.5">
        <span className={cn('text-sm leading-body', headerColor)}>
          {t('TEAMS_ROWS_PER_PAGE', { defaultValue: 'Rows per page' })}
        </span>
        <Select value={String(pageSize)} onValueChange={(value) => onPageSizeChange(Number(value))}>
          <SelectTrigger
            aria-label={t('TEAMS_ROWS_PER_PAGE', { defaultValue: 'Rows per page' })}
            className={cn(
              'h-9 w-auto gap-1 rounded-card border bg-true-white px-2 text-sm leading-body shadow-none focus:ring-0 dark:bg-card',
              borderColor,
              valueColor
            )}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent side="top" className="rounded-card border-grey-100 dark:border-border">
            {PAGE_SIZES.map((size) => (
              <SelectItem key={size} value={String(size)}>
                {size}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center gap-3">
        <span className={cn('text-sm leading-body', headerColor)}>
          {t('TEAMS_PAGINATION_RANGE', {
            defaultValue: '{{from}}–{{to}} of {{total}}',
            from: rangeStart,
            to: rangeEnd,
            total: totalCount,
          })}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label={t('GO_TO_FIRST_PAGE')}
            disabled={isFirstPage}
            onClick={() => onPageChange(0)}
            className={cn(pageButtonClassName, 'hidden lg:flex')}
          >
            <FastArrowLeft width={16} height={16} strokeWidth={1.5} className={valueColor} />
          </button>
          <button
            type="button"
            aria-label={t('TEAMS_PREVIOUS_PAGE', { defaultValue: 'Previous page' })}
            disabled={isFirstPage}
            onClick={() => onPageChange(Math.max(0, page - 1))}
            className={pageButtonClassName}
          >
            <NavArrowLeft width={16} height={16} strokeWidth={1.5} className={valueColor} />
          </button>
          <button
            type="button"
            aria-label={t('TEAMS_NEXT_PAGE', { defaultValue: 'Next page' })}
            disabled={isLastPage}
            onClick={() => onPageChange(Math.min(pageCount - 1, page + 1))}
            className={pageButtonClassName}
          >
            <NavArrowRight width={16} height={16} strokeWidth={1.5} className={valueColor} />
          </button>
          <button
            type="button"
            aria-label={t('GO_TO_LAST_PAGE')}
            disabled={isLastPage}
            onClick={() => onPageChange(pageCount - 1)}
            className={cn(pageButtonClassName, 'hidden lg:flex')}
          >
            <FastArrowRight width={16} height={16} strokeWidth={1.5} className={valueColor} />
          </button>
        </div>
      </div>
    </div>
  );
}
